import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { daysAgoKey } from '@coparent/shared/src/services/adminMetricsService';

const RANGE_OPTIONS = [30, 60, 90];

const PLATFORM_COLORS = {
  web: '#1B6B4A',
  ios: '#F5C842',
  android: '#F2886D',
  dauTotal: '#57534e',
  mau: '#3BA676',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const isInProgress = payload[0]?.payload?.inProgress;
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm space-y-1">
      <p className="font-semibold text-ink-800">
        {label}
        {isInProgress && <span className="text-ink-400 font-normal"> · today, in progress</span>}
      </p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-ink-500 flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: p.color }}
          />
          <span className="font-medium capitalize">{p.name}:</span>
          <span>{p.value?.toLocaleString() ?? 0}</span>
        </p>
      ))}
    </div>
  );
};

// Hollow ring on the in-progress (today) data point — signals to the reader
// that today's number will keep growing through end-of-day.
const InProgressDot = ({ cx, cy, payload, stroke }) => {
  if (!payload?.inProgress) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="white"
      stroke={stroke}
      strokeWidth={2}
    />
  );
};

// Recharts 3 emits legend entries in its own internal registration order, not
// <Line> declaration order, and ignores the `payload` prop — so the legend is
// rendered directly: platforms first, then the two dashed totals.
const LEGEND_ENTRIES = [
  { key: 'web', label: 'Web', color: PLATFORM_COLORS.web },
  { key: 'ios', label: 'iOS', color: PLATFORM_COLORS.ios },
  { key: 'android', label: 'Android', color: PLATFORM_COLORS.android },
  { key: 'dauTotal', label: 'Total DAU', color: PLATFORM_COLORS.dauTotal },
  { key: 'mau', label: 'MAU', color: PLATFORM_COLORS.mau },
];

// Clicking an entry hides that series. Hidden lines drop out of the Y-axis
// domain too, so hiding MAU rescales the chart to the DAU range instead of
// leaving the platform lines squashed along the bottom.
const ChartLegend = ({ hidden, onToggle }) => (
  <ul className="flex items-center justify-center gap-4 flex-wrap text-xs pt-2">
    {LEGEND_ENTRIES.map((e) => {
      const isHidden = hidden.includes(e.key);
      return (
        <li key={e.key}>
          <button
            type="button"
            onClick={() => onToggle(e.key)}
            aria-pressed={!isHidden}
            title={isHidden ? `Show ${e.label}` : `Hide ${e.label}`}
            className={`flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors ${
              isHidden ? 'text-ink-300 line-through' : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            <span
              className="inline-block w-2 h-2 rounded-full transition-opacity"
              style={{ backgroundColor: e.color, opacity: isHidden ? 0.3 : 1 }}
            />
            {e.label}
          </button>
        </li>
      );
    })}
  </ul>
);

const RangePicker = ({ value, onChange }) => (
  <div className="flex items-center gap-0.5 bg-surface-100 rounded-lg p-0.5">
    {RANGE_OPTIONS.map((d) => (
      <button
        key={d}
        type="button"
        onClick={() => onChange(d)}
        aria-pressed={value === d}
        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
          value === d
            ? 'bg-white text-ink-800 shadow-warm-sm'
            : 'text-ink-400 hover:text-ink-600'
        }`}
      >
        {d}d
      </button>
    ))}
  </div>
);

const DauMauChart = ({ stats }) => {
  const [rangeDays, setRangeDays] = useState(RANGE_OPTIONS[0]);
  const [hidden, setHidden] = useState([]);

  const toggleSeries = (key) =>
    setHidden((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  // `stats` arrives at the widest range; narrow to the selected window by date
  // rather than by array position, so gaps in the rollup can't stretch the
  // window further back than the label claims.
  const windowed = useMemo(() => {
    const cutoff = daysAgoKey(rangeDays);
    return (stats || []).filter((s) => (s.date || s.id || '') >= cutoff);
  }, [stats, rangeDays]);

  const data = windowed.map((s) => ({
    date: s.date?.slice(5) || s.id?.slice(5),
    web: s.dau?.web || 0,
    ios: s.dau?.ios || 0,
    android: s.dau?.android || 0,
    // Older docs predate the dau.total field — fall back to the sum.
    dauTotal:
      s.dau?.total ?? ((s.dau?.web || 0) + (s.dau?.ios || 0) + (s.dau?.android || 0)),
    mau: s.mau?.total || 0,
    inProgress: s.inProgress || false,
  }));

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">
          Daily active users by platform
        </h3>
        <RangePicker value={rangeDays} onChange={setRangeDays} />
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Unique signed-in users per day over the last {rangeDays} days, split by web / iOS / Android.
        Total DAU and MAU are the dashed lines — MAU is the trailing 30-day total.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#57534e' }}
            axisLine={{ stroke: '#e5e0d6' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#57534e' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            content={<ChartLegend hidden={hidden} onToggle={toggleSeries} />}
            wrapperStyle={{ paddingTop: 8 }}
          />
          <Line type="monotone" dataKey="web" name="Web" hide={hidden.includes('web')} stroke={PLATFORM_COLORS.web} strokeWidth={2.5} dot={<InProgressDot />} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="ios" name="iOS" hide={hidden.includes('ios')} stroke={PLATFORM_COLORS.ios} strokeWidth={2.5} dot={<InProgressDot />} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="android" name="Android" hide={hidden.includes('android')} stroke={PLATFORM_COLORS.android} strokeWidth={2.5} dot={<InProgressDot />} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="dauTotal" name="Total DAU" hide={hidden.includes('dauTotal')} stroke={PLATFORM_COLORS.dauTotal} strokeWidth={2} strokeDasharray="4 4" dot={<InProgressDot />} activeDot={{ r: 5 }} />
          <Line type="monotone" dataKey="mau" name="MAU" hide={hidden.includes('mau')} stroke={PLATFORM_COLORS.mau} strokeWidth={2} strokeDasharray="4 4" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DauMauChart;
